'use client';

import * as React from 'react';
import {
  Search,
  ShoppingCart,
  DollarSign,
  User,
  Repeat,
  Store,
  Globe,
  Zap,
  Loader2,
  X,
  ArrowRight,
  CornerDownLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { OrderStatusBadge } from '@/components/orders/order-status-badge';
import { PaymentStatusBadge } from '@/components/payments/payment-status-badge';
import { Badge } from '@/components/ui/badge';
import { formatMoney } from '@/lib/money';
import { useDebouncedFastSearch, SearchResult } from '@/lib/api/fast-search';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { useAccountManagerSearchAndJoin } from '@/lib/api/account-manager';

interface FastSearchProps {
  placeholder?: string;
  className?: string;
}

const ENTITY_CONFIG = {
  order: { icon: ShoppingCart, label: 'Orders', color: 'text-blue-500' },
  payment: { icon: DollarSign, label: 'Payments', color: 'text-emerald-500' },
  customer: { icon: User, label: 'Customers', color: 'text-violet-500' },
  subscription: { icon: Repeat, label: 'Subscriptions', color: 'text-orange-500' },
  store: { icon: Store, label: 'Stores', color: 'text-pink-500' },
  domain: { icon: Globe, label: 'Domains', color: 'text-cyan-500' },
} as const;

type EntityType = keyof typeof ENTITY_CONFIG;

const FILTER_TABS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'customer', label: 'Customers' },
  { value: 'order', label: 'Orders' },
  { value: 'payment', label: 'Payments' },
  { value: 'subscription', label: 'Subs' },
  { value: 'store', label: 'Stores' },
  { value: 'domain', label: 'Domains' },
];

export function FastSearch({ placeholder, className }: FastSearchProps) {
  const { t } = useTranslation('search');
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState('all');
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const { orgRoutes, navigate: appNavigate } = useOrgNavigate();
  const { user } = useUser();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const placeholderText = placeholder || t('placeholder');
  const { isSuperAdmin, isAccountManager, whitelabelScope } = useSessionClaims();
  // Same rule as `accountManager.searchByAnyIdAndJoin`: Tagada staff only, never partner staff.
  const isTagadaStaff = (isSuperAdmin || isAccountManager) && whitelabelScope === null;
  const accountManagerMutation = useAccountManagerSearchAndJoin();
  const { data: searchResults, isLoading } = useDebouncedFastSearch(query, 20, 300);

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setQuery('');
      setActiveFilter('all');
      setSelectedIndex(0);
    }
  }, []);

  const handleSelectResult = React.useCallback(
    (result: SearchResult) => {
      if (result.entityType === 'domain') {
        appNavigate(orgRoutes.DOMAINS());
        setOpen(false);
        setQuery('');
        return;
      }
      const routes: Record<string, (id: string) => string> = {
        order: orgRoutes.ORDER_DETAILS,
        payment: orgRoutes.PAYMENT_DETAILS,
        customer: orgRoutes.CUSTOMER_DETAILS,
        subscription: orgRoutes.SUBSCRIPTION_DETAILS,
        store: orgRoutes.STORE,
      };
      const routeFn = routes[result.entityType];
      if (routeFn) appNavigate(routeFn(result.entityId));
      setOpen(false);
      setQuery('');
    },
    [appNavigate, orgRoutes],
  );

  const handleQuickJump = React.useCallback(async () => {
    if (!user?.id || !query.trim()) return;
    const loadingToast = toast.loading(t('quickJumpLoading'));
    try {
      const result = await accountManagerMutation.mutateAsync({
        query: query.trim(),
        userId: user.id,
      });
      toast.dismiss(loadingToast);
      if (result.found && result.orgSlug) {
        // `found` only means the account resolved. The join can still have
        // failed, and its reason then rides in `message` — announcing that as a
        // success reads as "✅ Organization is full (7/7 seats)" (#TAG-2096).
        const joined = result.autoJoined || result.alreadyMember;
        (joined ? toast.success : toast.error)(result.message);
        setOpen(false);
        setQuery('');
        window.location.href = `/org/${result.orgSlug}`;
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error instanceof Error ? error.message : 'Failed to jump to organization');
    }
  }, [user?.id, query, accountManagerMutation, t]);

  const getFormattedDisplay = (result: SearchResult) => {
    const m = result.metadata;
    switch (result.entityType) {
      case 'customer':
        return {
          primary: `${m?.firstName || ''} ${m?.lastName || ''}`.trim() || result.entityId,
          secondary: m?.email,
          tertiary: m?.storeName,
          statusBadge: null,
        };
      case 'order':
        return {
          primary: t('entities.order', { id: result.entityId }),
          secondary: m?.customerName || m?.customerEmail,
          tertiary: m?.storeName,
          statusBadge: m?.status ? <OrderStatusBadge status={m.status} /> : null,
        };
      case 'payment': {
        const amount = m?.amount && m?.currency ? formatMoney(m.amount, m.currency) : '';
        return {
          primary: t('entities.payment', { id: result.entityId }),
          secondary: [amount, m?.customerName].filter(Boolean).join(' \u00B7 '),
          tertiary: m?.customerEmail,
          statusBadge: m?.status ? <PaymentStatusBadge status={m.status as any} /> : null,
        };
      }
      case 'subscription':
        return {
          primary: t('entities.subscription', { id: result.entityId }),
          secondary: m?.customerName || m?.customerEmail,
          tertiary: m?.storeName,
          statusBadge: m?.status ? (
            <Badge variant={m.status === 'active' ? 'green' : m.status === 'trialing' ? 'blue' : 'default'}>
              {m.status}
            </Badge>
          ) : null,
        };
      case 'store':
        return {
          primary: m?.name || result.entityId,
          secondary: m?.type?.toUpperCase(),
          tertiary: m?.baseCurrency,
          statusBadge: null,
        };
      case 'domain':
        return {
          primary: m?.domain || result.entityId,
          secondary: null,
          tertiary: null,
          statusBadge: m?.isVerified ? (
            <Badge variant="green">Verified</Badge>
          ) : (
            <Badge variant="default">Unverified</Badge>
          ),
        };
      default:
        return { primary: result.displayText, secondary: result.matchedContent, tertiary: null, statusBadge: null };
    }
  };

  const { groupedResults, entityCounts, filteredResults, totalCount } = React.useMemo(() => {
    if (!searchResults?.results) {
      return { groupedResults: {} as Record<string, SearchResult[]>, entityCounts: {} as Record<string, number>, filteredResults: [], totalCount: 0 };
    }
    const grouped: Record<string, SearchResult[]> = {};
    const counts: Record<string, number> = {};
    for (const r of searchResults.results) {
      if (!grouped[r.entityType]) grouped[r.entityType] = [];
      grouped[r.entityType].push(r);
      counts[r.entityType] = (counts[r.entityType] || 0) + 1;
    }
    const filtered = activeFilter === 'all'
      ? searchResults.results
      : (grouped[activeFilter] || []);
    return { groupedResults: grouped, entityCounts: counts, filteredResults: filtered, totalCount: searchResults.results.length };
  }, [searchResults, activeFilter]);

  React.useEffect(() => { setSelectedIndex(0); }, [filteredResults]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filteredResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && filteredResults.length > 0) {
        e.preventDefault();
        handleSelectResult(filteredResults[selectedIndex]);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, filteredResults, selectedIndex, handleSelectResult]);

  React.useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const hasQuery = query.length >= 2;
  const hasResults = hasQuery && !isLoading && totalCount > 0;
  const noResults = hasQuery && !isLoading && totalCount === 0;

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className={`group border-border/50 hover:border-border hover:bg-muted/60 flex h-10 w-full max-w-md items-center gap-2 rounded-lg border bg-transparent px-2.5 transition-all sm:h-8 ${className}`}
      >
        <Search className="text-muted-foreground/60 h-4 w-4 shrink-0 sm:h-3.5 sm:w-3.5" />
        <span className="text-muted-foreground/60 flex-1 truncate text-left text-sm sm:text-xs">{placeholderText}</span>
        <kbd className="border-border/60 bg-muted/50 text-muted-foreground/70 pointer-events-none hidden h-5 shrink-0 select-none items-center gap-0.5 rounded border px-1.5 font-mono text-[10px] font-medium sm:inline-flex">
          <span className="text-[11px]">⌘</span>K
        </kbd>
      </button>

      {/* Dialog */}
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            className="bg-card fixed inset-x-0 top-0 z-[10000] flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-hidden border shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 duration-200 sm:left-1/2 sm:right-auto sm:top-[min(20%,180px)] sm:h-auto sm:max-w-[560px] sm:-translate-x-1/2 sm:rounded-xl sm:data-[state=closed]:zoom-out-[0.98] sm:data-[state=open]:zoom-in-[0.98]"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <DialogPrimitive.Title className="sr-only">Search</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">Search across your data</DialogPrimitive.Description>

            {/* Input row */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center">
                {isLoading && hasQuery ? (
                  <Loader2 className="text-primary h-[18px] w-[18px] animate-spin" />
                ) : (
                  <Search className="text-muted-foreground/50 h-[18px] w-[18px]" />
                )}
              </div>
              <input
                ref={inputRef}
                placeholder={t('searchPlaceholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="placeholder:text-muted-foreground/50 h-8 min-w-0 flex-1 bg-transparent text-[15px] font-light outline-none"
                autoFocus
              />
              {query ? (
                <button
                  onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                  className="text-muted-foreground hover:text-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : (
                <kbd className="text-muted-foreground/50 shrink-0 text-[11px] font-medium">ESC</kbd>
              )}
            </div>

            <div className="bg-border h-px" />

            {/* Filter tabs — only when results exist */}
            {hasResults && (
              <>
                <div className="flex items-center gap-1 overflow-x-auto px-4 py-2">
                  {FILTER_TABS.map((tab) => {
                    const count = tab.value === 'all' ? totalCount : (entityCounts[tab.value] || 0);
                    const isActive = activeFilter === tab.value;
                    const isDisabled = tab.value !== 'all' && count === 0;
                    return (
                      <button
                        key={tab.value}
                        onClick={() => !isDisabled && setActiveFilter(tab.value)}
                        disabled={isDisabled}
                        className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                          isActive
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : isDisabled
                              ? 'text-muted-foreground/30 cursor-default'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      >
                        {tab.label}
                        {count > 0 && (
                          <span className={`tabular-nums ${isActive ? 'text-primary-foreground/70' : 'text-muted-foreground/50'}`}>
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="bg-border h-px" />
              </>
            )}

            {/* Content area */}
            <div className="flex-1 overflow-y-auto sm:max-h-[min(420px,60vh)] sm:flex-none" ref={listRef}>
              {/* Default state */}
              {query.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-14">
                  <div className="bg-muted/50 flex h-12 w-12 items-center justify-center rounded-full">
                    <Search className="text-muted-foreground/30 h-5 w-5" />
                  </div>
                  <div className="text-center">
                    <p className="text-muted-foreground text-sm">{t('startTyping')}</p>
                    <p className="text-muted-foreground/50 mt-1 text-xs">Search orders, payments, customers, and more</p>
                  </div>
                  {isTagadaStaff && (
                    <div className="mt-1 flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 dark:bg-amber-950/30">
                      <Zap className="h-3 w-3 text-amber-500" />
                      <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">{t('accountManagerHint')}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Minimum chars */}
              {query.length > 0 && query.length < 2 && (
                <div className="text-muted-foreground/60 py-14 text-center text-sm">
                  {t('typeAtLeast2')}
                </div>
              )}

              {/* Loading */}
              {isLoading && hasQuery && totalCount === 0 && (
                <div className="py-14 text-center">
                  <Loader2 className="text-muted-foreground/30 mx-auto mb-3 h-5 w-5 animate-spin" />
                  <p className="text-muted-foreground/60 text-sm">{t('searching')}</p>
                </div>
              )}

              {/* Account Manager Quick Jump */}
              {isTagadaStaff && hasQuery && !isLoading && (
                <div className="px-2 pt-2">
                  <button
                    onClick={handleQuickJump}
                    disabled={accountManagerMutation.isPending}
                    className="group/qj flex w-full items-center gap-3 rounded-lg border border-amber-200/60 bg-amber-50/50 px-3 py-2.5 text-left transition-all hover:border-amber-300 hover:bg-amber-50 disabled:opacity-60 dark:border-amber-800/40 dark:bg-amber-950/20 dark:hover:border-amber-700/50 dark:hover:bg-amber-950/30"
                  >
                    {accountManagerMutation.isPending ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-500" />
                    ) : (
                      <Zap className="h-4 w-4 shrink-0 text-amber-500" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                        {accountManagerMutation.isPending ? t('quickJumpSearching') : t('quickJumpTitle')}
                      </p>
                      {!accountManagerMutation.isPending && (
                        <p className="text-xs text-amber-600 dark:text-amber-500">
                          {t('quickJumpDescription', { query })}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-amber-400 opacity-0 transition-opacity group-hover/qj:opacity-100" />
                  </button>
                </div>
              )}

              {/* No results */}
              {noResults && (
                <div className="flex flex-col items-center gap-2 py-14">
                  <p className="text-muted-foreground text-sm">{t('noResults', { query })}</p>
                  {isTagadaStaff && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">{t('tryQuickJump')}</p>
                  )}
                </div>
              )}

              {/* Results list */}
              {hasResults && (
                <div className="p-1.5">
                  {activeFilter === 'all' ? (
                    Object.entries(groupedResults).map(([type, results]) => (
                      <ResultGroup
                        key={type}
                        type={type as EntityType}
                        results={results}
                        selectedIndex={selectedIndex}
                        globalOffset={getGlobalOffset(groupedResults, type)}
                        onSelect={handleSelectResult}
                        onHover={setSelectedIndex}
                        getFormattedDisplay={getFormattedDisplay}
                      />
                    ))
                  ) : (
                    <ResultGroup
                      type={activeFilter as EntityType}
                      results={filteredResults}
                      selectedIndex={selectedIndex}
                      globalOffset={0}
                      onSelect={handleSelectResult}
                      onHover={setSelectedIndex}
                      getFormattedDisplay={getFormattedDisplay}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {(hasResults || hasQuery) && (
              <>
                <div className="bg-border h-px" />
                <div className="flex items-center justify-between px-4 py-2">
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground/50 flex items-center gap-1 text-[11px]">
                      <kbd className="border-border/50 inline-flex h-4 items-center rounded border px-1 font-mono text-[10px]">↑↓</kbd>
                      navigate
                    </span>
                    <span className="text-muted-foreground/50 flex items-center gap-1 text-[11px]">
                      <kbd className="border-border/50 inline-flex h-4 items-center rounded border px-1 font-mono text-[10px]">
                        <CornerDownLeft className="h-2.5 w-2.5" />
                      </kbd>
                      open
                    </span>
                    <span className="text-muted-foreground/50 flex items-center gap-1 text-[11px]">
                      <kbd className="border-border/50 inline-flex h-4 items-center rounded border px-1 font-mono text-[10px]">esc</kbd>
                      close
                    </span>
                  </div>
                  {hasResults && (
                    <span className="text-muted-foreground/40 text-[11px] tabular-nums">
                      {totalCount} result{totalCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}

function getGlobalOffset(grouped: Record<string, SearchResult[]>, currentType: string): number {
  let offset = 0;
  for (const [type, results] of Object.entries(grouped)) {
    if (type === currentType) return offset;
    offset += results.length;
  }
  return offset;
}

interface ResultGroupProps {
  type: EntityType;
  results: SearchResult[];
  selectedIndex: number;
  globalOffset: number;
  onSelect: (result: SearchResult) => void;
  onHover: (index: number) => void;
  getFormattedDisplay: (result: SearchResult) => {
    primary: string;
    secondary: string | null | undefined;
    tertiary: string | null | undefined;
    statusBadge: React.ReactNode;
  };
}

function ResultGroup({ type, results, selectedIndex, globalOffset, onSelect, onHover, getFormattedDisplay }: ResultGroupProps) {
  const config = ENTITY_CONFIG[type];
  if (!config || !results.length) return null;

  return (
    <div className="mb-1 last:mb-0">
      <div className="text-muted-foreground/50 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider">
        {config.label}
      </div>
      {results.map((result, i) => {
        const globalIdx = globalOffset + i;
        const isSelected = globalIdx === selectedIndex;
        const display = getFormattedDisplay(result);
        const Icon = config.icon;

        return (
          <button
            key={`${result.entityType}-${result.entityId}-${i}`}
            data-index={globalIdx}
            onClick={() => onSelect(result)}
            onMouseEnter={() => onHover(globalIdx)}
            className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors ${
              isSelected ? 'bg-accent' : 'hover:bg-accent/50'
            }`}
          >
            <div className={`bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-lg`}>
              <Icon className={`h-4 w-4 ${config.color}`} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium">{display.primary}</span>
                {display.statusBadge}
              </div>
              {display.secondary && (
                <span className="text-muted-foreground truncate text-xs">{display.secondary}</span>
              )}
            </div>
            {isSelected && (
              <CornerDownLeft className="text-muted-foreground/40 h-3.5 w-3.5 shrink-0" />
            )}
          </button>
        );
      })}
    </div>
  );
}

export function FastSearchCompact({ placeholder, className }: FastSearchProps) {
  return <FastSearch placeholder={placeholder} className={`max-w-md ${className}`} />;
}
